import React, { useEffect, useRef, useState } from "react"
import { Navigate, useParams, useLocation, Link } from "react-router-dom"
import { Drawer } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faBars,
  faAnglesRight,
  faMagnifyingGlass,
  faCompass,
  faFileCircleQuestion,
} from "@fortawesome/free-solid-svg-icons"

import { Loader } from "shared/ui/Loader/Loader.component"
import { useUiStore } from "stores/uiStore"
import {
  findSection,
  findPage,
  pageRoute,
} from "features/Docs/manifest"
import { useDocsContent } from "features/Docs/hooks/useDocsContent"
import { useDocsSearch } from "features/Docs/hooks/useDocsSearch"
import DocsNav from "features/Docs/components/DocsNav.component"
import DocsToc from "features/Docs/components/DocsToc.component"
import DocsLanding from "features/Docs/components/DocsLanding.component"
import DocsMarkdown from "features/Docs/components/DocsMarkdown.component"
import DocsBreadcrumbs from "features/Docs/components/DocsBreadcrumbs.component"
import DocsPagerLinks from "features/Docs/components/DocsPagerLinks.component"
import DocsSearchModal from "features/Docs/components/DocsSearchModal.component"

import "styles/docs.css"

/** Friendly panel for an unknown section or page. */
const NotFoundPanel: React.FC = () => (
  <div className="docs-panel">
    <FontAwesomeIcon icon={faCompass} className="docs-panel__icon" />
    <h1 className="docs-panel__title">We couldn&rsquo;t find that page</h1>
    <p className="docs-panel__text">
      The documentation link may be out of date or mistyped. Head back to the
      docs home and pick up from there.
    </p>
    <Link to="/docs" className="docs-panel__action">
      Go to docs home
    </Link>
  </div>
)

/** Placeholder for a valid page whose markdown hasn't been authored yet. */
const UnwrittenPanel: React.FC<{ title: string }> = ({ title }) => (
  <div className="docs-panel">
    <FontAwesomeIcon
      icon={faFileCircleQuestion}
      className="docs-panel__icon"
    />
    <h1 className="docs-panel__title">{title}</h1>
    <p className="docs-panel__text">
      This page hasn&rsquo;t been written yet. It&rsquo;s on the map — check
      back soon, or explore the rest of the docs in the meantime.
    </p>
    <Link to="/docs" className="docs-panel__action">
      Browse the docs
    </Link>
  </div>
)

/**
 * Documentation shell: resolves the section/page from the URL, renders the
 * landing page, a redirect, a not-found panel, or the three-pane reading
 * layout (nav / article / on-this-page) as appropriate. Owns the Ctrl/Cmd+K
 * search palette and the mobile nav drawer.
 */
const DocsContainer: React.FC = () => {
  const { section: sectionSlug, page: pageSlug } = useParams<{
    section?: string
    page?: string
  }>()
  const location = useLocation()
  const contentQuery = useDocsContent()
  const content = contentQuery.data
  const { search } = useDocsSearch(content)

  const [searchOpen, setSearchOpen] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  // Desktop nav collapse (persisted across sessions). Independent of the mobile
  // drawer, the TOC, and search.
  const docsNavCollapsed = useUiStore((state) => state.docsNavCollapsed)
  const toggleDocsNav = useUiStore((state) => state.toggleDocsNav)
  const collapseBtnRef = useRef<HTMLButtonElement>(null)
  const expandBtnRef = useRef<HTMLButtonElement>(null)
  // Skip the mount pass so we don't steal focus on first render / rehydration;
  // only user-driven toggles should move focus between the two controls.
  const skipInitialFocus = useRef(true)

  // Keyboard focus handoff: collapsing moves focus to the expand button,
  // expanding moves it back to the collapse button.
  useEffect(() => {
    if (skipInitialFocus.current) {
      skipInitialFocus.current = false
      return
    }
    if (docsNavCollapsed) {
      expandBtnRef.current?.focus()
    } else {
      collapseBtnRef.current?.focus()
    }
  }, [docsNavCollapsed])

  // Global search shortcut — only while the docs are mounted.
  useEffect(() => {
    const handler = (event: KeyboardEvent): void => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault()
        setSearchOpen((prev) => !prev)
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [])

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setDrawerOpen(false)
  }, [location.pathname])

  // After content renders, honour a URL hash (scroll to heading) or reset to
  // the top of the article pane on a plain page change.
  useEffect(() => {
    if (location.hash) {
      const id = decodeURIComponent(location.hash.slice(1))
      const timer = window.setTimeout(() => {
        document.getElementById(id)?.scrollIntoView()
      }, 0)
      return () => window.clearTimeout(timer)
    }
    scrollRef.current?.scrollTo({ top: 0 })
  }, [location.hash, location.pathname, content])

  // Landing page: no section selected.
  if (!sectionSlug) {
    return (
      <div className="docs-root docs-root--landing">
        <DocsLanding onSearch={() => setSearchOpen(true)} />
        <DocsSearchModal
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          search={search}
        />
      </div>
    )
  }

  // Section chosen but no page: redirect to the section's first page.
  if (sectionSlug && !pageSlug) {
    const section = findSection(sectionSlug)
    if (section) {
      return (
        <Navigate
          replace
          to={pageRoute(section.slug, section.pages[0].slug)}
        />
      )
    }
  }

  const resolved = pageSlug ? findPage(sectionSlug, pageSlug) : undefined
  const parsed = resolved ? content?.get(resolved.page.file) : undefined
  const resolvedRoute = resolved
    ? pageRoute(resolved.section.slug, resolved.page.slug)
    : undefined

  return (
    <div className="docs-root">
      <aside
        className={`docs-root__nav${
          docsNavCollapsed ? " docs-root__nav--collapsed" : ""
        }`}
        inert={docsNavCollapsed || undefined}
      >
        <DocsNav
          onCollapse={toggleDocsNav}
          collapseButtonRef={collapseBtnRef}
        />
      </aside>

      <Drawer
        placement="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={280}
        className="docs-drawer"
        title="Documentation"
      >
        <DocsNav onNavigate={() => setDrawerOpen(false)} />
      </Drawer>

      <div className="docs-root__main">
        {docsNavCollapsed && (
          <div className="docs-nav-expand-bar">
            <button
              type="button"
              ref={expandBtnRef}
              className="docs-nav-expand"
              onClick={toggleDocsNav}
              aria-label="Show documentation navigation"
            >
              <FontAwesomeIcon icon={faAnglesRight} />
            </button>
          </div>
        )}

        <div className="docs-mobile-bar">
          <button
            type="button"
            className="docs-mobile-bar__menu"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open documentation menu"
          >
            <FontAwesomeIcon icon={faBars} />
            <span>Menu</span>
          </button>
          <button
            type="button"
            className="docs-mobile-bar__search"
            onClick={() => setSearchOpen(true)}
            aria-label="Search documentation"
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </button>
        </div>

        <div className="docs-root__scroll" ref={scrollRef}>
          <div className="docs-root__content">
            <article className="docs-article">
              {!resolved ? (
                <NotFoundPanel />
              ) : contentQuery.isLoading ? (
                <div className="docs-article__loading">
                  <Loader fontSize={40} color="#36C3ED" />
                </div>
              ) : !parsed ? (
                <UnwrittenPanel title={resolved.page.title} />
              ) : (
                <>
                  <DocsBreadcrumbs
                    section={resolved.section}
                    page={resolved.page}
                  />
                  <h1 className="docs-article__title">
                    {parsed.meta.title ?? resolved.page.title}
                  </h1>
                  {parsed.meta.description && (
                    <p className="docs-article__lede">
                      {parsed.meta.description}
                    </p>
                  )}
                  <DocsMarkdown body={parsed.body} />
                  {resolvedRoute && <DocsPagerLinks route={resolvedRoute} />}
                </>
              )}
            </article>

            {resolvedRoute && parsed && (
              <aside className="docs-root__toc">
                <DocsToc scrollRef={scrollRef} contentKey={resolvedRoute} />
              </aside>
            )}
          </div>
        </div>
      </div>

      <DocsSearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        search={search}
      />
    </div>
  )
}

export default DocsContainer
